import type { Lang } from "./i18n";
import { firstNonEmptyParam, paramsFromSearchAndHash } from "./urlParams";

const preservedQueryKeys = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "ad_source",
  "source",
  "entry_source",
  "gad_source",
  "gad_campaignid",
  "gclid",
  "gbraid",
  "wbraid",
  "twclid",
  "ttclid",
  "fbclid",
  "id",
  "email_id",
];

const adSourceValues = new Set([
  "google",
  "googlex",
  "google_ads",
  "googleads",
  "adwords",
  "google-ads",
  "x",
  "x_ads",
  "xads",
  "twitter",
  "twitter_ads",
  "twitter-ads",
  "tiktok",
  "tiktok_ads",
  "tiktokads",
  "tiktok-ads",
  "tt",
  "tt_ads",
  "meta",
  "meta_ads",
  "metaads",
  "facebook",
  "facebook_ads",
  "facebook-ads",
  "fb",
  "fb_ads",
  "instagram",
  "instagram_ads",
  "instagram-ads",
  "ig",
  "ig_ads",
]);

export const aiReceptionistRoot = "/ai-receptionist";

export function localeRootPath(lang: Lang) {
  return lang === "en" ? aiReceptionistRoot : `/cn${aiReceptionistRoot}`;
}

function preservedParams(search?: string) {
  const params = paramsFromSearchAndHash(search);
  const nextParams = new URLSearchParams();

  preservedQueryKeys.forEach((key) => {
    const value = firstNonEmptyParam(params, key);
    if (value !== null && value !== "") nextParams.set(key, value);
  });

  return nextParams;
}

function pathWithParams(path: string, params: URLSearchParams, hash = "") {
  const query = params.toString();
  return `${path}${query ? `?${query}` : ""}${hash}`;
}

function isAdSource(value: string | null) {
  return value ? adSourceValues.has(value.trim().toLowerCase()) : false;
}

export function localeRoot(lang: Lang, search?: string) {
  return pathWithParams(lang === "en" ? "/" : localeRootPath(lang), preservedParams(search));
}

export function sectionHref(lang: Lang, hash: string, search?: string) {
  return pathWithParams(lang === "en" ? "/" : localeRootPath(lang), preservedParams(search), hash);
}

export function pricingHref(lang: Lang, pathname: string, search?: string) {
  return /\/solutions\/dental-clinics\/?$/.test(pathname)
    ? pathWithParams(pathname, preservedParams(search), "#pricing")
    : sectionHref(lang, "#pricing", search);
}

export function leadFormPath(lang: Lang, email?: string, source?: string, search?: string) {
  return contactFormPath(`${localeRootPath(lang)}/contact`, email, source, search);
}

export const hongKongRootPath = "/hk/ai-receptionist";

/* 香港版聯絡頁：與英文/簡體表單共用 query 保留與 source 規則。 */
export function hongKongContactPath(email?: string, source?: string, search?: string) {
  return contactFormPath(`${hongKongRootPath}/contact`, email, source, search);
}

function contactFormPath(path: string, email?: string, source?: string, search?: string) {
  const params = preservedParams(search);
  const trimmedEmail = email?.trim();

  if (trimmedEmail) params.set("email_id", trimmedEmail);

  if (source) {
    if (isAdSource(params.get("source"))) {
      params.set("entry_source", source);
    } else {
      params.set("source", source);
    }
  }

  return pathWithParams(path, params);
}

export function demoPath(lang: Lang, search?: string) {
  return pathWithParams(`${localeRootPath(lang)}/demo/dental-booking`, preservedParams(search));
}

function productReturnPath(value: string) {
  if (value === "/" || value === "/en") return aiReceptionistRoot;
  if (value === "/en/ai-receptionist" || value === "/en/ai-receptionist/") return aiReceptionistRoot;
  if (value === "/cn") return `/cn${aiReceptionistRoot}`;
  if (value.startsWith("/en/")) return `${aiReceptionistRoot}/${value.slice(4)}`;
  if (value.startsWith("/cn/") && !value.startsWith(`/cn${aiReceptionistRoot}/`)) {
    return `/cn${aiReceptionistRoot}/${value.slice(4)}`;
  }
  return value;
}

function safeDownloadReturnPath(value: string | null | undefined) {
  if (!value) return null;
  try {
    const url = new URL(value, "https://clawease.ai");
    const normalizedPath = productReturnPath(url.pathname);
    const productPath = normalizedPath === aiReceptionistRoot
      || normalizedPath.startsWith(`${aiReceptionistRoot}/`)
      || normalizedPath === `/cn${aiReceptionistRoot}`
      || normalizedPath.startsWith(`/cn${aiReceptionistRoot}/`);
    if (url.origin !== "https://clawease.ai" || !productPath || /\/download\/?$/.test(normalizedPath)) return null;
    return `${normalizedPath}${url.hash}`;
  } catch {
    return null;
  }
}

export function downloadReturnPath(lang: Lang, search: string) {
  const params = paramsFromSearchAndHash(search);
  const explicitPath = safeDownloadReturnPath(params.get("return_to"));
  const dentalSource = [params.get("source"), params.get("entry_source")].some(
    (source) => source === "industry_dental-clinics_hero" || source?.startsWith("dental_"),
  );
  const path = explicitPath || (dentalSource || params.get("industry") === "dental"
    ? `${aiReceptionistRoot}/solutions/dental-clinics`
    : localeRootPath(lang));
  const url = new URL(path, "https://clawease.ai");
  return pathWithParams(url.pathname, preservedParams(search), url.hash);
}

function downloadParams(source: string, plan?: string, search?: string, returnTo?: string) {
  const params = preservedParams(search);
  if (isAdSource(params.get("source"))) {
    params.set("entry_source", source);
  } else {
    params.set("source", source);
  }
  if (plan) params.set("plan", plan);
  const returnPath = safeDownloadReturnPath(returnTo);
  if (returnPath) params.set("return_to", returnPath);
  return params;
}

export function downloadPath(lang: Lang, source: string, plan?: string, search?: string, returnTo?: string) {
  const params = downloadParams(source, plan, search, returnTo);
  return pathWithParams(`${localeRootPath(lang)}/download`, params);
}

/**
 * The Hong Kong landing page downloads the same desktop application as the
 * production root site. Keep its non-JavaScript fallback URL on the canonical
 * `/ai-receptionist/download` route instead of exposing the unrelated `/cn/`
 * locale. DownloadLink still resolves the installer in place via the API.
 */
export function hongKongDownloadPath(source: string, plan?: string, search?: string) {
  return pathWithParams(`${aiReceptionistRoot}/download`, downloadParams(source, plan, search));
}
